import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { Button } from "@components/Button";
import { CenterAlignedTitle } from "@components/CenterAlignedTitle";
import { ReservationHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import {
  InformationPanel,
  InformationPanelItem,
  InformationPanelStrongLinkItem,
} from "@components/InformationPanel";
import { LeftAlignedTitle } from "@components/LeftAlignedTitle";
import LoginRequiredLayout from "@components/LoginRequiredLayout";
import NameInput from "@components/NameInput";
import PhoneNumberInput from "@components/PhoneNumberInput";
import ReservationLeavingModal from "@components/ReservationLeavingModal";
import { ReservationTelModal } from "@components/ReservationTelModal";
import { Text } from "@components/Text";
import Toaster from "@components/Toaster";
import { RequestReservationSteps } from "@components/pages/shops/[uuid]/request_reservation/RequestReservationSteps";
import { ReservationComesAtRow } from "@components/pages/shops/[uuid]/request_reservation/ReservationFormComesAtRow";
import { ReservationFormDateRow } from "@components/pages/shops/[uuid]/request_reservation/ReservationFormDateRow";
import { ReservationFormMatchOptionRow } from "@components/pages/shops/[uuid]/request_reservation/ReservationFormMatchOptionRow";
import { ReservationFormPersonNumRow } from "@components/pages/shops/[uuid]/request_reservation/ReservationFormPersonNumRow";
import { ReservationFormPlanRow } from "@components/pages/shops/[uuid]/request_reservation/ReservationFormPlanRow";
import { ReservationFormRow } from "@components/pages/shops/[uuid]/request_reservation/ReservationFormRow";
import { ReservationFormSupportingTeamRow } from "@components/pages/shops/[uuid]/request_reservation/ReservationFormSupportingTeamRow";
import { ReservationFormUserMessage } from "@components/pages/shops/[uuid]/request_reservation/ReservationFormUserMessage";
import { StaticPaymentMethodEnum } from "@components/pages/shops/[uuid]/request_reservation/payment_methods/ReservationPaymentForm";
import { ReservationPlanAllowedPaymentTypeEnum } from "@graphql/__generated__/types";
import Auth from "@hooks/useAuth";
import Message, { PredefinedMessages } from "@hooks/useMessage";
import { useModalDialog } from "@hooks/useModalDialog";
import RequestReservationContainer from "@hooks/useRequestReservation";
import useStopPageTransition from "@hooks/useStopPageTransition";
import { paths } from "@libs/paths";
import {
  useRequestReservation_MaximumPointsToGiveEachFrameLazyQuery,
  useRequestReservation_ReservationFramesByDateOrMatchLazyQuery,
  useRequestReservation_ReservationMatchOptionsByDateLazyQuery,
  useRequestReservation_ReservationVisitDatesQuery,
} from "./__generated__/queries";
import useFormReset from "./_useFormReset";
import useQueryParams from "./_useQueryParams";
import useRequestReservationForm from "./_useRequestReservationForm";
import useSyncQueryParams from "./_useSyncQueryParams";
import styles from "./index.module.css";
import type { RequestReservationInputForm } from "./_useRequestReservationForm";
import type { NextPage } from "next";

export const notSelectedValue = "";

const RequestReservation: NextPage = () => {
  const router = useRouter();
  const confirmModal = useModalDialog();
  const telModal = useModalDialog();

  const { shopUuid } = useQueryParams();

  const {
    methods,
    selectedDateOptionValue,
    selectedDate,
    selectedMatchId,
    selectedSupportingTeamId,
    selectedFrameId,
    selectedPersonNum,
    triggerName,
  } = useRequestReservationForm();
  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const { addMessage } = Message.useContainer();
  const { fanstaUser } = Auth.useContainer();
  /**
   * エラー発生時のナビゲーションフラグ
   * handleError で店舗詳細ページに遷移する際に true になり、useSyncQueryParams での URL 更新を抑制する
   */
  const hasNavigationErrorRef = useRef(false);
  /**
   * 人数レンジ算出用フレーム (fetchAllFrames) の取得済み date/match キー。
   * personNum の値変更のたびに同一クエリ (personNum: null) を再発火させないためのガード。
   * date/match が変わったときのみ再取得する (オーナーの上限変更を再選択で反映するため network-only は維持)。
   */
  const allFramesFetchKeyRef = useRef<string | null>(null);
  const handleError = () => {
    hasNavigationErrorRef.current = true;
    addMessage(PredefinedMessages.requestReservationNoAvailableFramesError);
    forcePageTransition(paths.shopDetail(shopUuid));
  };

  const { data: datesData } = useRequestReservation_ReservationVisitDatesQuery({
    variables: { shopUuid },
    skip: !shopUuid,
    onError: handleError,
  });
  const [fetchMatchOptions, { data: matchOptionsData }] =
    useRequestReservation_ReservationMatchOptionsByDateLazyQuery();
  useEffect(() => {
    // skipオプションだとselectedDateが初期化処理で空になったときにクエリがキャンセルされるのでuseEffectとLazyQueryを使用している
    if (!shopUuid || !selectedDate) {
      return;
    }
    fetchMatchOptions({
      variables: { shopUuid, date: selectedDate ?? "" },
      onError: handleError,
    });
  }, [shopUuid, selectedDate]);

  const matchOptions = matchOptionsData?.reservationMatchOptionsByDate ?? [];
  const isSingleOptionWithoutMatch =
    matchOptions.length === 1 && matchOptions[0].match === null;
  const selectedMatch = matchOptions.find((mo) =>
    selectedMatchId === notSelectedValue
      ? !mo.match
      : mo.match?.id.toString() === selectedMatchId
  )?.match;
  const homeTeam = selectedMatch?.homeTeam;
  const awayTeam = selectedMatch?.awayTeam;

  const [fetchFrames, { data: framesData, loading: framesLoading }] =
    useRequestReservation_ReservationFramesByDateOrMatchLazyQuery();
  // 人数選択肢のレンジ算出用に personNum フィルタなしの全フレームを取得する。
  // フレーム取得クエリ (fetchFrames) は選択中の personNum で絞り込まれ、
  // API のフィルタ (min_person_num <= personNum <= max_person_num) により
  // 上限プランが除外されて ceiling が不安定になりうるため、レンジ算出用の入力を分離する。
  const [fetchAllFrames, { data: allFramesData }] =
    useRequestReservation_ReservationFramesByDateOrMatchLazyQuery();
  const [fetchPoints, { data: pointsData, loading: pointsLoading }] =
    useRequestReservation_MaximumPointsToGiveEachFrameLazyQuery();
  const loading = framesLoading || pointsLoading;
  useEffect(() => {
    // skipオプションだとselectedMatchIdが初期化処理でundefinedになったときにクエリがキャンセルされるのでuseEffectとLazyQueryを使用している
    if (
      !shopUuid ||
      !selectedDate ||
      selectedMatchId === undefined ||
      selectedMatchId === null
    ) {
      return;
    }
    fetchFrames({
      variables: {
        shopUuid,
        date: selectedMatchId ? null : selectedDate,
        matchId: selectedMatchId ? Number(selectedMatchId) : null,
        personNum: selectedPersonNum ? Number(selectedPersonNum) : null,
      },
      onError: handleError,
    });
    if (selectedPersonNum) {
      fetchPoints({
        variables: {
          shopUuid,
          date: selectedMatchId ? null : selectedDate,
          matchId: selectedMatchId ? Number(selectedMatchId) : null,
          personNum: Number(selectedPersonNum),
        },
        onError: handleError,
      });
      // personNum 選択後は fetchFrames の結果が絞り込まれるため、
      // 人数選択肢のレンジ算出用に personNum フィルタなしのフレームを別途取得する。
      // (personNum 未選択時は fetchFrames の結果が既に未フィルタなのでそちらを使う)
      // personNum の値変更では vars (personNum: null) が変わらないため、
      // date/match 単位でガードして同一クエリの再発火を防ぐ。
      const allFramesFetchKey = selectedMatchId
        ? `match:${selectedMatchId}`
        : `date:${selectedDate}`;
      if (allFramesFetchKeyRef.current !== allFramesFetchKey) {
        allFramesFetchKeyRef.current = allFramesFetchKey;
        fetchAllFrames({
          variables: {
            shopUuid,
            date: selectedMatchId ? null : selectedDate,
            matchId: selectedMatchId ? Number(selectedMatchId) : null,
            personNum: null,
          },
          onError: handleError,
        });
      }
    }
    // 試合の選択肢の中に、クエリパラメータで指定した試合IDに紐づく試合が存在しない場合は店舗詳細に戻す。
    //
    // isInitializedがないと初期化処理中にselectedMatchIdとselectedMatchの値が一時的に不整合になるため、誤ってエラー処理が走ってしまう。
    // また、matchOptionsDataがないとselectedMatchの算出が正しく行われない場合があるため、これも条件に加える。
    // cf. https://github.com/mixi-sb/fansta-doc/issues/8141
    if (
      isInitialized &&
      matchOptionsData &&
      selectedMatchId !== notSelectedValue &&
      !selectedMatch
    ) {
      handleError();
      return;
    }
  }, [
    shopUuid,
    selectedDate,
    selectedMatchId,
    selectedPersonNum,
    selectedMatch,
  ]);

  const frames = framesData?.reservationFramesByDateOrMatch || [];
  const allFrames = allFramesData?.reservationFramesByDateOrMatch || [];
  // 人数選択肢のレンジは personNum で絞り込まれていない全フレームから算出する。
  // personNum 未選択時 (allFrames 未取得) は frames が既に未フィルタなのでそのまま使う。
  const personNumRangeFrames = allFrames.length > 0 ? allFrames : frames;
  const selectedFrame = frames.find((f) => f.id === Number(selectedFrameId));
  const hasEventReservationPlan = frames.some(
    (frame) => frame.reservationPlan.isEvent
  );
  const currentFanstaUser = fanstaUser?.currentUser.user;
  const shouldShowEmailVerificationNotice =
    hasEventReservationPlan &&
    !!currentFanstaUser &&
    !currentFanstaUser.emailVerified;
  const shop = datesData?.shop;
  const dates = datesData?.reservationVisitDates ?? [];
  const cancelFeePercentage = datesData?.onSystemPaymentCancelFee.percentage;
  const pointsToGiveFrames =
    pointsData?.maximumPointsToGiveEachFrame.items ?? [];

  const [isInitialized, setIsInitialized] = useState(false);
  /**
   * matchId確定待ちフラグ（page_view二重発火防止用）
   * 日付変更後、新しいmatchOptionsDataが取得されるまでtrue
   * 同じレンダリングサイクル内でも最新値を参照可能（useEffectのガード条件で使用）
   */
  const isMatchIdPendingRef = useRef(false);

  useFormReset(
    methods,
    datesData,
    matchOptionsData,
    selectedFrameId,
    selectedMatchId,
    selectedDate,
    isInitialized,
    setIsInitialized,
    isMatchIdPendingRef
  );

  const { forcePageTransition } = useStopPageTransition(
    !!selectedDate,
    [
      paths.shopRequestReservationConfirm(shopUuid),
      paths.shopRequestReservationPaymentMethods(shopUuid),
      paths.shopRequestReservation(shopUuid),
    ],
    async () => {
      return await confirmModal.show();
    }
  );

  useSyncQueryParams(
    isInitialized,
    selectedDateOptionValue,
    selectedDate,
    selectedMatchId,
    isMatchIdPendingRef,
    hasNavigationErrorRef
  );

  const { setRequestReservation, setRequestReservationFramesForGA } =
    RequestReservationContainer.useContainer();
  const onSubmit = (input: RequestReservationInputForm) => {
    if (!selectedFrame || !shop || !setRequestReservation) {
      return;
    }
    // 名前が入力済みの場合、triggerを発火させるタイミングが無いため、確認ボタン押下のタイミングで発火させる
    triggerName();

    const skipPaymentMethods =
      selectedFrame.reservationPlan?.allowedPaymentType ===
      ReservationPlanAllowedPaymentTypeEnum.OnSite;

    setRequestReservation({
      shop,
      reservationFrame: selectedFrame,
      pointsToGiveFrame: pointsToGiveFrames.find(
        (p) => p.reservationFrameId === selectedFrame.id
      ),
      comesAt: input.comesAt,
      personNum: Number(input.personNum),
      userMessage: input.userMessage?.trim() ?? "",
      supportingTeamId: Number(input.supportingTeamId),
      paymentMethod: skipPaymentMethods
        ? StaticPaymentMethodEnum.OnSite
        : undefined,
      lastName: input.lastName ?? "",
      lastNameKana: input.lastNameKana ?? "",
      firstName: input.firstName ?? "",
      firstNameKana: input.firstNameKana ?? "",
      phoneNumber: input.phoneNumber ?? "",
      confirmationToken: undefined,
      creditCard: undefined,
      savesNewCard: false,
      pointsToApply: undefined,
      cancelFeePercentage,
    });
    setRequestReservationFramesForGA(frames);
    if (skipPaymentMethods) {
      router.push(paths.shopRequestReservationConfirm(shopUuid));
    } else {
      router.push(paths.shopRequestReservationPaymentMethods(shopUuid));
    }
  };

  const hasTbaTeam = !homeTeam || !awayTeam;
  const isConfirmationButtonEnabled = selectedMatchId
    ? isValid && (hasTbaTeam || selectedSupportingTeamId !== notSelectedValue)
    : isValid;

  return (
    <CommonLayout hasFooter footerPC>
      <ReservationHead title="リクエスト予約" shop={shop} />
      <LoginRequiredLayout>
        <Toaster
          messageFilter={[
            PredefinedMessages.requestReservationPaymentError,
            PredefinedMessages.requestReservationNoAvailableFramesError,
          ]}
        />
        <CenterAlignedTitle label="リクエスト予約" />
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <RequestReservationSteps
              step={1}
              showsStep2={shop?.canReserveOnSystemPayment}
            />
            {hasEventReservationPlan && (
              <div className={styles.eventReservationNotice}>
                <InformationPanel isFloating={false}>
                  {shouldShowEmailVerificationNotice && (
                    <InformationPanelItem
                      type="caution"
                      closeButton={false}
                      isAutoClose={false}
                    >
                      <div>
                        <div className={styles.eventReservationNoticeEmailText}>
                          ご登録のメールアドレス認証が未完了の場合、当選のご連絡ができない可能性があります。
                        </div>
                        <InformationPanelStrongLinkItem
                          text="メールアドレス認証をする"
                          href={paths.mypageEmail}
                          isNewTab
                        />
                      </div>
                    </InformationPanelItem>
                  )}
                  <InformationPanelItem
                    type="caution"
                    closeButton={false}
                    isAutoClose={false}
                  >
                    <div>
                      抽選に必要な追加情報（例：同行者情報等）のご回答をお願いする場合があります。追加情報の入力フォームはメールでお送りします。
                    </div>
                  </InformationPanelItem>
                </InformationPanel>
              </div>
            )}
            <div className={styles.title}>
              <LeftAlignedTitle label={"ご予約詳細入力"} />
            </div>
            <div className={styles.rows}>
              <ReservationFormRow title="ご予約の店舗">
                <Text size={14} className={styles.formDataLabel}>
                  {shop?.name || ""}
                </Text>
              </ReservationFormRow>
              {dates.length > 0 && <ReservationFormDateRow dates={dates} />}
              {selectedDate && (
                <>
                  {matchOptionsData && !isSingleOptionWithoutMatch && (
                    <ReservationFormMatchOptionRow
                      matchOptions={matchOptions}
                    />
                  )}
                  {selectedMatch && (
                    <ReservationFormSupportingTeamRow
                      homeTeam={homeTeam}
                      awayTeam={awayTeam}
                    />
                  )}
                  <ReservationFormPersonNumRow frames={personNumRangeFrames} />
                  <ReservationFormPlanRow
                    loading={loading}
                    frames={frames}
                    pointsToGiveFrames={pointsToGiveFrames}
                    launchTelModal={telModal.show}
                    canUseOnSystemPayment={
                      shop?.canReserveOnSystemPayment ?? false
                    }
                    cancelFeePercentage={cancelFeePercentage}
                  />
                  {!loading && frames.length > 0 && (
                    <ReservationComesAtRow
                      reservationFrame={selectedFrame}
                      showNote={!selectedMatchId}
                    />
                  )}
                </>
              )}
            </div>

            {selectedDate && !loading && frames.length > 0 && (
              <>
                <NameInput request required />
                <div className={styles.phoneNumber}>
                  <PhoneNumberInput required />
                </div>
                {selectedFrame?.reservationPlan.acceptsUserMessage && (
                  <div className={styles.userMessage}>
                    <ReservationFormUserMessage
                      hint={selectedFrame.reservationPlan.userMessageHint}
                      isEvent={selectedFrame.reservationPlan.isEvent}
                    />
                  </div>
                )}
                <Button
                  className={styles.btn}
                  styleType="filled"
                  colorType="primary"
                  type="submit"
                  disabled={!isConfirmationButtonEnabled}
                >
                  次へ
                </Button>
                <div className={styles.link}>
                  <Link href={paths.shopDetail(shop?.uuid || "")}>
                    リクエスト予約をやめる
                  </Link>
                </div>
              </>
            )}
          </form>
        </FormProvider>
        <ReservationTelModal
          isShown={telModal.isShown}
          onClose={telModal.abortHandler}
          tel={shop?.telWithHyphen ?? ""}
        />
        <ReservationLeavingModal
          isShown={confirmModal.isShown}
          onLeave={confirmModal.continueHandler}
          onStay={confirmModal.abortHandler}
        />
      </LoginRequiredLayout>
    </CommonLayout>
  );
};

export default RequestReservation;
